import {
  ChartBarIcon,
  ChatIcon,
  DotsThreeIcon,
  HeartIcon,
  RepeatIcon,
  SealCheckIcon,
  UploadSimpleIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { MediaSlot } from './MediaSlot';
import { PreviewAvatar } from './PreviewAvatar';
import {
  DEFAULT_PREVIEW_BRAND_COLOR,
  type PlatformPreviewBaseProps,
  compactNumber,
  previewTimestampLabel,
  resolveAspectRatio,
  resolveCarouselAspectRatio,
} from './types';
import { useCarousel } from './useCarousel';

import { getPlatformBrandColorClass } from '@/lib/platforms/identity';
import { TEXT_FADE_RIGHT } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

// X's own brand identity (src/lib/platforms/identity.ts) is monochrome, not
// the legacy Twitter blue — hashtags/mentions should follow that, not a
// hardcoded hex.
const HASHTAG_CLASS = getPlatformBrandColorClass('x');

/**
 * X (Twitter) post — header row (avatar + name + handle + time + ⋯), body
 * text with hashtag/mention highlighting, optional photo, action row
 * (reply, repost, like, views, share).
 */
export const XPost: React.FC<PlatformPreviewBaseProps> = ({
  brandName,
  username,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  isVerified = false,
  caption,
  imageUrl,
  imageUrls,
  imageAspectRatios,
  mediaItems,
  carouselActiveIndex,
  onCarouselActiveIndexChange,
  videoUrl,
  videoPoster,
  aspectRatio,
  focalX,
  focalY,
  zoom,
  timestamp,
  engagement,
  isLoading = false,
  mediaOverlay,
  className,
}) => {
  const handle = (username || brandName).toLowerCase().replace(/\s+/g, '');
  // Real X renders 2-4 images as a grid, never a one-at-a-time carousel —
  // only fall into the single-image/video MediaSlot path (with its carousel
  // fallback) when there ISN'T a multi-image grid to show instead.
  const gridImages = !videoUrl && imageUrls && imageUrls.length > 1 ? imageUrls : null;
  const { currentImage, carouselOverlay, safeActive } = useCarousel(
    gridImages ? undefined : imageUrls,
    imageUrl,
    {
      activeIndex: carouselActiveIndex,
      onActiveIndexChange: onCarouselActiveIndexChange,
      mediaItems,
    },
  );
  const hasMedia = !gridImages && (!!currentImage || !!videoUrl || isLoading);
  // Must consult `imageAspectRatios`, not just `aspectRatio`. The composer
  // sends the per-child array and OMITS the scalar when it wants the media's
  // own ratio to drive the frame; a replica that reads only the scalar silently
  // falls back to its platform default and shows a crop the platform will not
  // apply — a 1080x1350 portrait rendered as a 1.91 landscape strip.
  const resolvedAspect = resolveAspectRatio(
    resolveCarouselAspectRatio(aspectRatio, imageAspectRatios, safeActive),
    1.91,
  );
  const time = previewTimestampLabel(timestamp);
  const replies = engagement?.comments;
  const reposts = engagement?.shares;
  const likes = engagement?.likes;
  const views = engagement?.views;

  return (
    <article
      className={cn('w-full bg-card border border-border/60 rounded-xl overflow-hidden', className)}
      data-platform="x-post"
    >
      <div className="flex gap-3 p-3">
        <PreviewAvatar
          brandName={brandName}
          brandColor={brandColor}
          avatarUrl={avatarUrl}
          className="size-10 rounded-full"
          textClassName="text-caption font-semibold"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 min-w-0">
            {/* Long display names fade out instead of "…" (Mora overflow rule),
                capped so they can't crowd out the @handle / time. */}
            <p
              className={cn(
                'min-w-0 max-w-[60%] text-sm font-semibold text-foreground',
                TEXT_FADE_RIGHT,
              )}
            >
              {brandName}
            </p>
            {isVerified ? (
              <SealCheckIcon className="size-3.5 shrink-0 text-platform-x-verified" weight="fill" />
            ) : null}
            <span className={cn('min-w-0 text-sm text-muted-foreground', TEXT_FADE_RIGHT)}>
              @{handle}
            </span>
            <span className="text-muted-foreground/40">·</span>
            <span className="text-sm text-muted-foreground shrink-0">{time}</span>
            <DotsThreeIcon className="ml-auto size-5 text-foreground/60 shrink-0" weight="bold" />
          </div>

          {caption ? (
            <p className="mt-0.5 text-sm leading-relaxed text-foreground whitespace-pre-wrap break-words">
              {formatBody(caption)}
            </p>
          ) : null}

          {gridImages ? (
            <XImageGrid images={gridImages} alt={caption ?? `${brandName} post`} />
          ) : hasMedia ? (
            <MediaSlot
              ratio={resolvedAspect}
              bg="muted"
              imageUrl={currentImage}
              videoUrl={videoUrl}
              videoPoster={videoPoster}
              focalX={focalX}
              focalY={focalY}
              zoom={zoom}
              isLoading={isLoading}
              alt={caption ?? `${brandName} post`}
              overlay={
                mediaOverlay ? (
                  <>
                    {carouselOverlay}
                    {mediaOverlay}
                  </>
                ) : (
                  carouselOverlay
                )
              }
              className="mt-2 rounded-2xl overflow-hidden border border-border/60"
            />
          ) : null}

          <div className="mt-2 flex items-center justify-between text-muted-foreground">
            <XAction
              icon={<ChatIcon className="size-4" weight="regular" />}
              label={replies == null ? undefined : compactNumber(replies)}
            />
            <XAction
              icon={<RepeatIcon className="size-4" weight="regular" />}
              label={reposts == null ? undefined : compactNumber(reposts)}
            />
            <XAction
              icon={<HeartIcon className="size-4" weight="regular" />}
              label={likes == null ? undefined : compactNumber(likes)}
            />
            <XAction
              icon={<ChartBarIcon className="size-4" weight="regular" />}
              label={views == null ? undefined : compactNumber(views)}
            />
            <button
              type="button"
              aria-label="Share"
              className="p-1.5 rounded-full hover:bg-muted/50 transition-colors"
            >
              <UploadSimpleIcon className="size-4" weight="regular" />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

/**
 * X's real 2-4 image grid layout: 2 = side by side, 3 = one large left +
 * two stacked right, 4 = even 2x2. X caps posts at 4 images, so anything
 * beyond that is dropped rather than adding a "+N" overflow chip the real
 * platform doesn't show at this stage (that only appears once posted).
 */
const XImageGrid: React.FC<{ images: string[]; alt: string }> = ({ images, alt }) => {
  const shown = images.slice(0, 4);
  const count = shown.length;

  return (
    <div
      className={cn(
        'mt-2 grid gap-0.5 aspect-[16/9] rounded-2xl overflow-hidden border border-border/60',
        count === 2 && 'grid-cols-2',
        count >= 3 && 'grid-cols-2 grid-rows-2',
      )}
    >
      {shown.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={`${alt} ${i + 1}`}
          loading="lazy"
          className={cn('size-full object-cover', count === 3 && i === 0 && 'row-span-2')}
        />
      ))}
    </div>
  );
};

const XAction: React.FC<{ icon: React.ReactNode; label?: string }> = ({ icon, label }) => (
  <button
    type="button"
    className="flex items-center gap-1.5 text-detail rounded-full px-1.5 py-1 hover:bg-muted/50 hover:text-foreground transition-colors"
  >
    {icon}
    {label != null ? <span>{label}</span> : null}
  </button>
);

function formatBody(text: string): React.ReactNode {
  const parts = text.split(/(#\w+|@\w+)/g);
  return parts.map((part, i) => {
    if (part.startsWith('#') || part.startsWith('@')) {
      return (
        <span key={i} className={HASHTAG_CLASS}>
          {part}
        </span>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}
